/* Drag / swipe / horizontal-scroll to move through the Shop-the-look reels.
   Works on desktop (mouse drag + trackpad) and mobile (touch drag): a horizontal
   drag scrolls through the videos (one reel per ~80px of travel), driving the
   existing <reel-carousel> (and its <macaje-reels> subclass, see
   assets/shop-the-look.js) via its public go(). Vertical drags still scroll the
   page; a simple tap still centres a reel. Loaded once (defer).

   Also owns the play/pause toggle on the centre reel - see wirePlayback(). */
(function () {
  var STEP = 80; // px of horizontal travel per one reel step

  /* --- Clarity: make the paused side reels look as crisp as the centre. ---
     theme.js fades the off-centre reels (opacity 0.92 / 0.55) and every paused
     reel shows Shopify's low-res "_small" poster until a real frame is painted.
     We (a) force every visible reel to full opacity and (b) nudge each paused
     video's currentTime so the browser decodes + paints a full-res frame over
     the poster. The reels stay paused - only the centre ever plays. */
  function clarify(item) {
    var op = item.style.getPropertyValue('--op').trim();
    // Leave the centre (1) and the fully-hidden back reel (0) alone; lift the rest.
    if (op !== '' && op !== '0' && op !== '1') item.style.setProperty('--op', '1');
  }

  function primeFrame(v) {
    if (!v || v.__primed) return;
    function paint() {
      if (v.__primed) return;
      try {
        // Only a still, never-played reel needs it; a played one already holds a frame.
        if (v.readyState >= 2 && v.paused && !v.currentTime) {
          v.__primed = true;
          v.currentTime = 0.05;
        }
      } catch (e) {}
    }
    if (v.readyState >= 2) paint();
    else v.addEventListener('loadeddata', paint, { once: true });
  }

  function enhance(c) {
    var items = c.querySelectorAll('.reels__item');
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        clarify(item);
        primeFrame(item.querySelector('video'));
        // Re-assert full opacity every time theme.js re-runs its layout().
        if (window.MutationObserver) {
          new MutationObserver(function () { clarify(item); })
            .observe(item, { attributes: true, attributeFilter: ['style'] });
        }
      })(items[i]);
    }
  }

  /* --- Play/pause toggle on the centre reel. ------------------------------
     The markup already ships BOTH icons and the section CSS already swaps them
     on .reels__item[data-playing]. Two things were missing:

       1. the control was inert - .reels__playbtn is a <span aria-hidden="true">
          with pointer-events: none, so a click never reached it;
       2. data-playing tracked which reel was CENTRED, not whether the video was
          actually playing, so the icon never changed when playback stopped.

     Binding data-playing to the video's own play/pause events makes the icon
     correct no matter who changed playback - this script, a tap, or theme.js.
     The control is only made interactive here, so it stays inert if this script
     never runs rather than looking clickable and doing nothing. */
  function iconFade() {
    if (document.getElementById('reel-playbtn-fade')) return;
    var st = document.createElement('style');
    st.id = 'reel-playbtn-fade';
    // display:none/block cannot animate, so the incoming icon fades itself in.
    st.textContent =
      '.reels__playbtn svg { animation: reel-icon-in 160ms ease both; }' +
      '@keyframes reel-icon-in { from { opacity: 0; transform: scale(.8) } ' +
      'to { opacity: 1; transform: scale(1) } }' +
      '@media (prefers-reduced-motion: reduce) { .reels__playbtn svg { animation: none } }';
    document.head.appendChild(st);
  }

  function wirePlayback(c) {
    iconFade();
    var items = c.querySelectorAll('.reels__item');
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        var v = item.querySelector('video');
        var btn = item.querySelector('.reels__playbtn');
        if (!v || !btn || btn.__pbWired) return;
        btn.__pbWired = true;

        function sync() {
          var want = v.paused ? 'false' : 'true';
          if (item.getAttribute('data-playing') !== want) item.setAttribute('data-playing', want);
          btn.setAttribute('aria-label', v.paused ? 'Play video' : 'Pause video');
        }
        v.addEventListener('play', sync);
        v.addEventListener('pause', sync);
        v.addEventListener('ended', sync);

        // theme.js also writes data-playing on every layout(); if it disagrees
        // with the real state, put it back. Correcting to the true value makes
        // the next callback agree, so this settles rather than looping.
        if (window.MutationObserver) {
          new MutationObserver(sync).observe(item, {
            attributes: true, attributeFilter: ['data-playing']
          });
        }

        // Inert in the markup on purpose - promote it to a real control now.
        btn.style.pointerEvents = 'auto';
        btn.style.cursor = 'pointer';
        btn.removeAttribute('aria-hidden');
        btn.setAttribute('role', 'button');
        btn.setAttribute('tabindex', '0');
        sync();

        function toggle(e) {
          // An off-centre reel keeps its tap-to-centre behaviour, which plays it.
          if (item.getAttribute('data-center') !== 'true') return;
          e.preventDefault();
          e.stopPropagation();
          if (v.paused) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        }
        btn.addEventListener('click', toggle);
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') toggle(e);
        });
      })(items[i]);
    }
  }

  function wire(c) {
    if (!c || c.__swipeWired) return;
    c.__swipeWired = true;

    // Grab affordance on desktop; stop text/image drag-selection mid-swipe.
    c.style.cursor = 'grab';
    c.style.userSelect = 'none';
    c.style.webkitUserSelect = 'none';

    var x0 = 0, y0 = 0, lastX = 0;
    var active = false, decided = false, horizontal = false, moved = false, pid = null;

    function advance(dir) {
      if (c.items && typeof c.go === 'function') { c.go(dir); moved = true; }
    }

    function down(x, y, id) {
      x0 = x; y0 = y; lastX = x;
      active = true; decided = false; horizontal = false; moved = false; pid = id;
      c.style.cursor = 'grabbing';
    }

    function move(x, y, id) {
      if (!active || (pid != null && id != null && id !== pid)) return;
      if (!decided) {
        var adx = Math.abs(x - x0), ady = Math.abs(y - y0);
        if (adx < 8 && ady < 8) return;           // wait until intent is clear
        decided = true;
        horizontal = adx > ady;                    // vertical -> let the page scroll
        if (horizontal && id != null && c.setPointerCapture) {
          try { c.setPointerCapture(id); } catch (e) {}
        }
      }
      if (!horizontal) return;
      var d = x - lastX;
      while (Math.abs(d) >= STEP) {
        advance(d < 0 ? 1 : -1);                    // drag left = next, right = prev
        lastX += (d < 0 ? -STEP : STEP);
        d = x - lastX;
      }
    }

    function up(id) {
      if (!active) return;
      active = false;
      c.style.cursor = 'grab';
      if (pid != null && c.releasePointerCapture) { try { c.releasePointerCapture(pid); } catch (e) {} }
      pid = null;
    }

    if (window.PointerEvent) {
      c.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        down(e.clientX, e.clientY, e.pointerId);
      });
      c.addEventListener('pointermove', function (e) { move(e.clientX, e.clientY, e.pointerId); });
      c.addEventListener('pointerup', function (e) { up(e.pointerId); });
      c.addEventListener('pointercancel', function (e) { up(e.pointerId); });
    } else {
      c.addEventListener('touchstart', function (e) {
        if (e.touches.length === 1) down(e.touches[0].clientX, e.touches[0].clientY, null);
      }, { passive: true });
      c.addEventListener('touchmove', function (e) {
        if (e.touches.length === 1) move(e.touches[0].clientX, e.touches[0].clientY, null);
      }, { passive: true });
      c.addEventListener('touchend', function () { up(null); }, { passive: true });
    }

    // A drag ends in a click; swallow it so it doesn't also re-centre a reel.
    c.addEventListener('click', function (e) {
      if (moved) { moved = false; e.stopPropagation(); e.preventDefault(); }
    }, true);

    // Trackpad / horizontal mouse wheel.
    var lock = false;
    c.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 18) {
        e.preventDefault();
        if (lock) return;
        lock = true;
        setTimeout(function () { lock = false; }, 260);
        advance(e.deltaX > 0 ? 1 : -1);
      }
    }, { passive: false });

    // Crisp, full-opacity side reels (paint a real frame, lift the fade).
    enhance(c);
    wirePlayback(c);
  }

  function init() {
    var els = document.querySelectorAll('reel-carousel, macaje-reels');
    for (var i = 0; i < els.length; i++) wire(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
